import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiHome, FiBook, FiVideo, FiAward, FiUser, FiSettings, FiPlay, FiDownload, 
  FiStar, FiTrendingUp, FiClock, FiCheckCircle, FiBarChart2, FiUsers, 
  FiFilter, FiSearch, FiBookOpen, FiTarget, FiFlag, FiMessageSquare, 
  FiEye, FiThumbsUp, FiPlus
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import EducationalParticles from '../../components/common/EducationalParticles';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CourseCard from '../../components/common/CourseCard';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  learnerDashboardAPI,
  learnerCourseAPI,
  learnerBadgeAPI,
  learnerProgressAPI,
  learnerUtilityAPI,
} from '../../api/learnerEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  EmptyState,
  FilterBar,
  FilterSelect,
  SearchInput,
  ContentGrid,
} from './Dashboard.styles';

const LearnerDashboard = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentCourses, setRecentCourses] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [badges, setBadges] = useState([]);
  const [grades, setGrades] = useState([]);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [newBadge, setNewBadge] = useState(null);

  const menuItems = getLearnerMenu();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, badgesRes, gradesRes] = await Promise.all([
        learnerDashboardAPI.getDashboard(),
        learnerBadgeAPI.getMyBadges(),
        learnerUtilityAPI.getGrades(),
      ]);
      
      setDashboardData(dashboardRes.data);
      setRecentCourses(dashboardRes.data.recent_activity || []);
      setRecommendedCourses(dashboardRes.data.recommended_courses || []);
      setBadges(badgesRes.data.badges || []);
      setGrades(gradesRes.data.grades || []);
      
      // Check if this is first login and show welcome modal
      if (badgesRes.data.badges?.length === 0) {
        setShowWelcomeModal(true);
      }
    } catch (error) {
      showToast('error', 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomeBadge = async () => {
    try {
      const response = await learnerBadgeAPI.awardWelcomeBadge();
      setNewBadge(response.data.badge);
      setShowWelcomeModal(false);
      showToast('success', response.data.message);
      fetchDashboardData(); // Refresh to show new badge
    } catch (error) {
      showToast('error', 'Failed to award welcome badge');
    }
  };

  const handleStartCourse = async (courseId) => {
    try {
      await learnerCourseAPI.startCourse(courseId);
      showToast('success', 'Course started! Your progress will be tracked.');
      fetchDashboardData();
    } catch (error) {
      showToast('error', 'Failed to start course');
    }
  };

  const handleCompleteCourse = async (courseId) => {
    try {
      await learnerCourseAPI.completeCourse(courseId);
      showToast('success', 'Congratulations! Course completed!');
      fetchDashboardData();
    } catch (error) {
      showToast('error', 'Failed to complete course');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getGradeName = (gradeId) => {
    const grade = grades.find(g => g.id === gradeId);
    return grade ? grade.name : 'Unknown Grade';
  };


  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading your dashboard..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Welcome Back, Learner! 🎓</h1>
            <p>Continue your learning journey and discover new opportunities</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button 
              leftIcon={<FiBook />} 
              onClick={() => navigate('/learner/courses')}
            >
              My Courses
            </Button>
            <Button 
              leftIcon={<FiVideo />} 
              variant="ghost"
              onClick={() => navigate('/learner/browse')}
            >
              Browse Courses
            </Button>
          </div>
        </DashboardHeader>

        {/* Statistics */}
        {dashboardData?.statistics && (
          <StatsGrid>
            <StatCard>
              <StatIcon color="#2c5f34">
                <FiBook />
              </StatIcon>
              <StatInfo>
                <h3>{dashboardData.statistics.started_courses || 0}</h3>
                <p>Started Courses</p>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon color="#10B981">
                <FiCheckCircle />
              </StatIcon>
              <StatInfo>
                <h3>{dashboardData.statistics.completed_courses || 0}</h3>
                <p>Completed Courses</p>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon color="#F59E0B">
                <FiTarget />
              </StatIcon>
              <StatInfo>
                <h3>{dashboardData.statistics.passed_quizzes || 0}</h3>
                <p>Passed Quizzes</p>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon color="#EF4444">
                <FiAward />
              </StatIcon>
              <StatInfo>
                <h3>{dashboardData.statistics.badges_earned || 0}</h3>
                <p>Badges Earned</p>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon color="#8B5CF6">
                <FiBarChart2 />
              </StatIcon>
              <StatInfo>
                <h3>{dashboardData.statistics.average_quiz_score?.toFixed(1) || 0}%</h3>
                <p>Average Quiz Score</p>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon color="#06B6D4">
                <FiClock />
              </StatIcon>
              <StatInfo>
                <h3>{dashboardData.statistics.total_quiz_attempts || 0}</h3>
                <p>Quiz Attempts</p>
              </StatInfo>
            </StatCard>
          </StatsGrid>
        )}

        {/* Recent Activity */}
        <SectionTitle>Recent Activity</SectionTitle>
        {recentCourses.length === 0 ? (
          <EmptyState>
            <FiBook size={48} />
            <h3>No recent activity</h3>
            <p>Start exploring courses to see your learning progress here</p>
            <Button leftIcon={<FiVideo />} onClick={() => navigate('/learner/browse')}>
              Browse Courses
            </Button>
          </EmptyState>
        ) : (
          <ContentGrid>
            {recentCourses.map((activity) => (
              <CourseCard
                key={activity.course_id}
                course={{
                  ...activity,
                  id: activity.course_id,
                  title: activity.course_title,
                  is_completed: activity.is_completed,
                  customContent: (
                    <div className="meta">
                      <span>📅 {formatDate(activity.last_updated)}</span>
                      <span>📊 {activity.is_completed ? '100%' : 'In Progress'}</span>
                    </div>
                  )
                }}
                showProgress={false}
                showRating={false}
                showInstructor={false}
                showGrade={false}
              />
            ))}
          </ContentGrid>
        )}

        {/* Recommended Courses */}
        {recommendedCourses.length > 0 && (
          <>
            <SectionTitle style={{ marginTop: '40px' }}>Recommended for You</SectionTitle>
            <ContentGrid>
              {recommendedCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={{
                    ...course,
                    recommended: true
                  }}
                  showProgress={false}
                  showRating={true}
                  showInstructor={true}
                  showGrade={true}
                />
              ))}
            </ContentGrid>
          </>
        )}

        {/* Recent Badges */}
        {badges.length > 0 && (
          <>
            <SectionTitle style={{ marginTop: '40px' }}>Recent Badges</SectionTitle>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '20px',
            }}>
              {badges.slice(0, 4).map((badge) => (
                <div 
                  key={badge.id}
                  style={{
                    background: theme.colors.surface,
                    border: `2px solid ${theme.colors.border}`,
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                    {badge.badge?.icon || '🏆'}
                  </div>
                  <h4 style={{ margin: '0 0 8px 0', color: theme.colors.textPrimary }}>
                    {badge.badge?.name}
                  </h4>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '12px', 
                    color: theme.colors.textSecondary 
                  }}>
                    {formatDate(badge.earned_at)}
                  </p>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center' }}>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/learner/badges')}
              >
                View All Badges
              </Button>
            </div>
            {/* Recommended Courses (show 3) */}
            {recommendedCourses.length > 0 && (
              <>
                <SectionTitle style={{ marginTop: '32px' }}>Recommended for You</SectionTitle>
                <ContentGrid>
                  {recommendedCourses.slice(0, 3).map((course) => (
                    <CourseCard
                      key={course.id}
                      course={{
                        ...course,
                        recommended: true
                      }}
                      showProgress={false}
                      showRating={true}
                      showInstructor={true}
                      showGrade={true}
                    />
                  ))}
                </ContentGrid>
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <Button onClick={() => navigate('/learner/browse')}>
                    Browse More Courses
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </DashboardContainer>

      {/* Welcome Modal */}
      <Modal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        title="Welcome to Your Learning Journey! 🎉"
        size="medium"
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🚀</div>
          <h3 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary }}>
            Welcome Voyager!
          </h3>
          <p style={{ margin: '0 0 24px 0', color: theme.colors.textSecondary }}>
            You've just earned your first badge! This is the beginning of your amazing learning journey.
          </p>
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Button onClick={handleWelcomeBadge}>
              Claim Your Badge! 🏆
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setShowWelcomeModal(false)}
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </Modal>

      {/* New Badge Modal */}
      <Modal
        isOpen={!!newBadge}
        onClose={() => setNewBadge(null)}
        title="New Badge Earned! 🎉"
        size="small"
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>
            {newBadge?.icon || '🏆'}
          </div>
          <h3 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary }}>
            {newBadge?.name}
          </h3>
          <p style={{ margin: '0 0 24px 0', color: theme.colors.textSecondary }}>
            Congratulations! You've earned a new badge for your achievements.
          </p>
          <Button onClick={() => setNewBadge(null)}>
            Awesome! 🎉
          </Button>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default LearnerDashboard;
